import React, { useState, useEffect, useCallback, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { authFetch } from "../utils/authFetch";

interface AIExpansionDisplayProps {
  ideaId?: string | number;
  onApprove?: (expansionId: number) => void;
  onReject?: (expansionId: number) => void;
}

interface SWOTItem {
  point?: string;
  explanation?: string;
}

interface Persona {
  name?: string;
  demographics?: string;
  jobTitle?: string;
  industry?: string;
  goals?: string[];
  painPoints?: string[];
  howIdeaHelps?: string;
  communicationChannels?: string[];
}

interface Alternative {
  title?: string;
  description?: string;
  keyDifferences?: string[];
  advantages?: string[];
  targetMarketChanges?: string;
  implementationComplexity?: string;
}

interface ExpansionContent {
  strengths?: SWOTItem[];
  weaknesses?: SWOTItem[];
  opportunities?: SWOTItem[];
  threats?: SWOTItem[];
  personas?: Persona[];
  mainStatement?: string;
  keyBenefits?: string[];
  differentiationFactors?: string[];
  targetAudience?: string;
  valueMetrics?: string[];
  alternatives?: Alternative[];
  [key: string]: unknown;
}

interface Expansion {
  id: number;
  expansion_type: string;
  content: ExpansionContent;
  status: string;
  reviewed_by?: number;
  reviewer_name?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface StatusData {
  hasExpansions: boolean;
  expansionCount: number;
}

interface ExpansionCompleteEvent extends CustomEvent {
  detail: {
    ideaId: string | number;
  };
}

const AIExpansionDisplay: React.FC<AIExpansionDisplayProps> = ({
  ideaId,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [editingExpansion, setEditingExpansion] = useState<number | null>(null);
  const [editContent, setEditContent] = useState<ExpansionContent>({});

  const fetchExpansions = useCallback(async () => {
    try {
      console.log("AIExpansionDisplay: Fetching expansions from API...");
      setLoading(true);
      const response = await authFetch(
        `/api/kanban/ideas/${ideaId}/expansions`,
      );
      const data = (await response.json()) as ApiResponse<Expansion[]>;

      console.log("AIExpansionDisplay: API response:", data);

      if (data.success) {
        console.log(
          `AIExpansionDisplay: Loaded ${data.data?.length || 0} expansions`,
        );
        setExpansions(data.data || []);
        setError(null);
      } else {
        console.error(
          "AIExpansionDisplay: API error:",
          data.error || data.message,
        );
        setError(data.error || data.message || t("aiExpansion.failedToLoad"));
      }
    } catch (err) {
      console.error("AIExpansionDisplay: Fetch error:", err);
      setError(t("aiExpansion.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [ideaId]);

  useEffect(() => {
    if (ideaId) {
      console.log("AIExpansionDisplay: Fetching expansions for idea", ideaId);
      fetchExpansions();
    }
  }, [ideaId, fetchExpansions]);

  useEffect(() => {
    // Listen for AI expansion completion events
    const handleExpansionComplete = (event: Event) => {
      const customEvent = event as ExpansionCompleteEvent;
      console.log(
        "AIExpansionDisplay: Received expansion complete event",
        customEvent.detail,
      );
      if (customEvent.detail.ideaId === ideaId) {
        console.log("AIExpansionDisplay: Refreshing expansions for this idea");
        fetchExpansions();
      }
    };

    window.addEventListener("aiExpansionComplete", handleExpansionComplete);

    return () => {
      window.removeEventListener(
        "aiExpansionComplete",
        handleExpansionComplete,
      );
    };
  }, [ideaId, fetchExpansions]);

  const handleApprove = async (expansionId: number, event?: MouseEvent) => {
    // Prevent event propagation to avoid modal closing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      const response = await authFetch(
        `/api/kanban/expansions/${expansionId}/approve`,
        {
          method: "POST",
        },
      );

      const data = (await response.json()) as ApiResponse<unknown>;
      if (data.success) {
        // Refresh expansions
        await fetchExpansions();
        if (onApprove) onApprove(expansionId);
      } else {
        alert(
          t("aiExpansion.failedToApproveWithError", {
            error: data.error || data.message,
          }),
        );
      }
    } catch (err) {
      console.error("Error approving expansion:", err);
      alert(t("aiExpansion.failedToApprove"));
    }
  };

  const handleReject = async (expansionId: number, event?: MouseEvent) => {
    // Prevent event propagation to avoid modal closing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      const response = await authFetch(
        `/api/kanban/expansions/${expansionId}/reject`,
        {
          method: "POST",
        },
      );

      const data = (await response.json()) as ApiResponse<unknown>;
      if (data.success) {
        // Refresh expansions
        await fetchExpansions();
        if (onReject) onReject(expansionId);
      } else {
        alert(
          t("aiExpansion.failedToRejectWithError", {
            error: data.error || data.message,
          }),
        );
      }
    } catch (err) {
      console.error("Error rejecting expansion:", err);
      alert(t("aiExpansion.failedToReject"));
    }
  };

  const handleEdit = (expansion: Expansion, event?: MouseEvent) => {
    console.log(
      "AIExpansionDisplay: Starting edit mode for expansion",
      expansion.id,
    );

    // Prevent event propagation to avoid modal closing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setEditingExpansion(expansion.id);
    setEditContent({ ...expansion.content });
  };

  const handleSaveEdit = async (expansionId: number, event?: MouseEvent) => {
    console.log(
      "AIExpansionDisplay: Saving edit for expansion",
      expansionId,
      "with content:",
      editContent,
    );

    // Prevent event propagation to avoid modal closing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      const response = await authFetch(
        `/api/kanban/expansions/${expansionId}/update`,
        {
          method: "PUT",
          body: JSON.stringify({ content: editContent }),
        },
      );

      const data = (await response.json()) as ApiResponse<unknown>;
      console.log("AIExpansionDisplay: Save response:", data);

      if (data.success) {
        console.log("AIExpansionDisplay: Save successful, exiting edit mode");
        setEditingExpansion(null);
        setEditContent({});
        await fetchExpansions();
        alert(t("aiExpansion.changesSaved"));
      } else {
        console.error(
          "AIExpansionDisplay: Save failed:",
          data.error || data.message,
        );
        alert(
          t("aiExpansion.failedToSaveWithError", {
            error: data.error || data.message,
          }),
        );
      }
    } catch (err) {
      console.error("AIExpansionDisplay: Save error:", err);
      alert(t("aiExpansion.failedToSave"));
    }
  };

  const handleCancelEdit = (event?: MouseEvent) => {
    console.log("AIExpansionDisplay: Canceling edit mode");

    // Prevent event propagation to avoid modal closing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setEditingExpansion(null);
    setEditContent({});
  };

  const toggleSection = (sectionId: string, event?: MouseEvent) => {
    // Prevent event propagation to avoid modal closing
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const renderSWOTAnalysis = (expansion: Expansion) => {
    const content =
      editingExpansion === expansion.id ? editContent : expansion.content;
    const sectionId = `swot-${expansion.id}`;
    const isExpanded = expandedSections.has(sectionId);
    const isEditing = editingExpansion === expansion.id;

    const updateSWOTItem = (
      category: string,
      index: number,
      field: string,
      value: string,
    ) => {
      const newContent = { ...editContent };
      const categoryArray = newContent[category] as SWOTItem[] | undefined;
      if (!categoryArray)
        (newContent as Record<string, SWOTItem[]>)[category] = [];
      const arr = (newContent as Record<string, SWOTItem[]>)[category];
      if (!arr[index]) arr[index] = {};
      (arr[index] as Record<string, string>)[field] = value;
      setEditContent(newContent);
    };

    return (
      <div className="ai-expansion-section">
        <button
          type="button"
          className="expansion-header"
          onClick={(e) => toggleSection(sectionId, e)}
        >
          <h4>{t("aiExpansion.swotAnalysis")}</h4>
          <span className="expand-icon">
            {isExpanded ? "\u25BC" : "\u25B6"}
          </span>
        </button>

        {isExpanded && (
          <section
            className="expansion-content"
            aria-label={t("aiExpansion.ariaSwotContent")}
          >
            <div className="swot-grid">
              <div className="swot-quadrant strengths">
                <h5>{t("aiExpansion.strengths")}</h5>
                <ul>
                  {content.strengths?.map((item, index) => (
                    <li key={`strength-${index}-${item.point || ""}`}>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={item.point || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "strengths",
                                index,
                                "point",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t(
                              "aiExpansion.placeholders.strengthPoint",
                            )}
                          />
                          <textarea
                            value={item.explanation || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "strengths",
                                index,
                                "explanation",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-textarea"
                            placeholder={t(
                              "aiExpansion.placeholders.explanation",
                            )}
                            rows={2}
                          />
                        </>
                      ) : (
                        <>
                          <strong>{item.point}</strong>
                          <p>{item.explanation}</p>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="swot-quadrant weaknesses">
                <h5>{t("aiExpansion.weaknesses")}</h5>
                <ul>
                  {content.weaknesses?.map((item, index) => (
                    <li key={`weakness-${index}-${item.point || ""}`}>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={item.point || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "weaknesses",
                                index,
                                "point",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t(
                              "aiExpansion.placeholders.weaknessPoint",
                            )}
                          />
                          <textarea
                            value={item.explanation || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "weaknesses",
                                index,
                                "explanation",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-textarea"
                            placeholder={t(
                              "aiExpansion.placeholders.explanation",
                            )}
                            rows={2}
                          />
                        </>
                      ) : (
                        <>
                          <strong>{item.point}</strong>
                          <p>{item.explanation}</p>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="swot-quadrant opportunities">
                <h5>{t("aiExpansion.opportunities")}</h5>
                <ul>
                  {content.opportunities?.map((item, index) => (
                    <li key={`opportunity-${index}-${item.point || ""}`}>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={item.point || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "opportunities",
                                index,
                                "point",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t(
                              "aiExpansion.placeholders.opportunityPoint",
                            )}
                          />
                          <textarea
                            value={item.explanation || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "opportunities",
                                index,
                                "explanation",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-textarea"
                            placeholder={t(
                              "aiExpansion.placeholders.explanation",
                            )}
                            rows={2}
                          />
                        </>
                      ) : (
                        <>
                          <strong>{item.point}</strong>
                          <p>{item.explanation}</p>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="swot-quadrant threats">
                <h5>{t("aiExpansion.threats")}</h5>
                <ul>
                  {content.threats?.map((item, index) => (
                    <li key={`threat-${index}-${item.point || ""}`}>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={item.point || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "threats",
                                index,
                                "point",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t(
                              "aiExpansion.placeholders.threatPoint",
                            )}
                          />
                          <textarea
                            value={item.explanation || ""}
                            onChange={(e) =>
                              updateSWOTItem(
                                "threats",
                                index,
                                "explanation",
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-textarea"
                            placeholder={t(
                              "aiExpansion.placeholders.explanation",
                            )}
                            rows={2}
                          />
                        </>
                      ) : (
                        <>
                          <strong>{item.point}</strong>
                          <p>{item.explanation}</p>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}
      </div>
    );
  };

  const renderPersonas = (expansion: Expansion) => {
    const content =
      editingExpansion === expansion.id ? editContent : expansion.content;
    const sectionId = `personas-${expansion.id}`;
    const isExpanded = expandedSections.has(sectionId);
    const isEditing = editingExpansion === expansion.id;

    const updatePersonaField = (
      personaIndex: number,
      field: string,
      value: string,
    ) => {
      const newContent = { ...editContent };
      if (!newContent.personas) newContent.personas = [];
      if (!newContent.personas[personaIndex])
        newContent.personas[personaIndex] = {};
      (newContent.personas[personaIndex] as Record<string, string>)[field] =
        value;
      setEditContent(newContent);
    };

    const updatePersonaArrayField = (
      personaIndex: number,
      field: string,
      arrayIndex: number,
      value: string,
    ) => {
      const newContent = { ...editContent };
      if (!newContent.personas) newContent.personas = [];
      if (!newContent.personas[personaIndex])
        newContent.personas[personaIndex] = {};
      const persona = newContent.personas[personaIndex] as Record<
        string,
        string[]
      >;
      if (!persona[field]) persona[field] = [];
      persona[field][arrayIndex] = value;
      setEditContent(newContent);
    };

    return (
      <div className="ai-expansion-section">
        <button
          type="button"
          className="expansion-header"
          onClick={(e) => toggleSection(sectionId, e)}
        >
          <h4>{t("aiExpansion.userPersonas")}</h4>
          <span className="expand-icon">
            {isExpanded ? "\u25BC" : "\u25B6"}
          </span>
        </button>

        {isExpanded && (
          <section
            className="expansion-content"
            aria-label={t("aiExpansion.ariaPersonasContent")}
          >
            <div className="personas-grid">
              {content.personas?.map((persona, index) => (
                <div
                  key={`persona-${index}-${persona.name || ""}`}
                  className="persona-card"
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={persona.name || ""}
                        onChange={(e) =>
                          updatePersonaField(index, "name", e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        placeholder={t("aiExpansion.placeholders.personaName")}
                      />
                      <input
                        type="text"
                        value={persona.demographics || ""}
                        onChange={(e) =>
                          updatePersonaField(
                            index,
                            "demographics",
                            e.target.value,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        placeholder={t("aiExpansion.placeholders.demographics")}
                      />
                      <input
                        type="text"
                        value={`${persona.jobTitle || ""} - ${persona.industry || ""}`}
                        onChange={(e) => {
                          const parts = e.target.value.split(" - ");
                          updatePersonaField(index, "jobTitle", parts[0] || "");
                          updatePersonaField(index, "industry", parts[1] || "");
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        placeholder={t(
                          "aiExpansion.placeholders.jobTitleIndustry",
                        )}
                      />

                      <div className="persona-section">
                        <strong>{t("aiExpansion.goals")}</strong>
                        {persona.goals?.map((goal, i) => (
                          <input
                            key={`goal-${i}-${goal || ""}`}
                            type="text"
                            value={goal || ""}
                            onChange={(e) =>
                              updatePersonaArrayField(
                                index,
                                "goals",
                                i,
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t("aiExpansion.indexedGoal", {
                              index: i + 1,
                            })}
                          />
                        ))}
                      </div>

                      <div className="persona-section">
                        <strong>{t("aiExpansion.painPoints")}</strong>
                        {persona.painPoints?.map((pain, i) => (
                          <input
                            key={`pain-${i}-${pain || ""}`}
                            type="text"
                            value={pain || ""}
                            onChange={(e) =>
                              updatePersonaArrayField(
                                index,
                                "painPoints",
                                i,
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t("aiExpansion.indexedPainPoint", {
                              index: i + 1,
                            })}
                          />
                        ))}
                      </div>

                      <div className="persona-section">
                        <strong>{t("aiExpansion.howIdeaHelps")}</strong>
                        <textarea
                          value={persona.howIdeaHelps || ""}
                          onChange={(e) =>
                            updatePersonaField(
                              index,
                              "howIdeaHelps",
                              e.target.value,
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="edit-textarea"
                          placeholder={t("aiExpansion.placeholders.howHelps")}
                          rows={3}
                        />
                      </div>

                      <div className="persona-section">
                        <strong>
                          {t("aiExpansion.communicationChannels")}
                        </strong>
                        {persona.communicationChannels?.map((channel, i) => (
                          <input
                            key={`channel-${i}-${channel || ""}`}
                            type="text"
                            value={channel || ""}
                            onChange={(e) =>
                              updatePersonaArrayField(
                                index,
                                "communicationChannels",
                                i,
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t("aiExpansion.indexedChannel", {
                              index: i + 1,
                            })}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <h5>{persona.name}</h5>
                      <p className="demographics">{persona.demographics}</p>
                      <p className="job-title">
                        {persona.jobTitle} - {persona.industry}
                      </p>

                      <div className="persona-section">
                        <strong>{t("aiExpansion.goals")}</strong>
                        <ul>
                          {persona.goals?.map((goal, i) => (
                            <li key={`goal-li-${i}-${goal}`}>{goal}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="persona-section">
                        <strong>{t("aiExpansion.painPoints")}</strong>
                        <ul>
                          {persona.painPoints?.map((pain, i) => (
                            <li key={`pain-li-${i}-${pain}`}>{pain}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="persona-section">
                        <strong>{t("aiExpansion.howIdeaHelps")}</strong>
                        <p>{persona.howIdeaHelps}</p>
                      </div>

                      <div className="persona-section">
                        <strong>
                          {t("aiExpansion.communicationChannels")}
                        </strong>
                        <div className="channels">
                          {persona.communicationChannels?.map((channel, i) => (
                            <span
                              key={`channel-tag-${i}-${channel}`}
                              className="channel-tag"
                            >
                              {channel}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  };

  const renderUVP = (expansion: Expansion) => {
    const content =
      editingExpansion === expansion.id ? editContent : expansion.content;
    const sectionId = `uvp-${expansion.id}`;
    const isExpanded = expandedSections.has(sectionId);
    const isEditing = editingExpansion === expansion.id;

    const updateUVPField = (field: string, value: string) => {
      const newContent = { ...editContent };
      (newContent as Record<string, string>)[field] = value;
      setEditContent(newContent);
    };

    const updateUVPArrayField = (
      field: string,
      arrayIndex: number,
      value: string,
    ) => {
      const newContent = { ...editContent };
      const arr = (newContent as Record<string, string[]>)[field];
      if (!arr) (newContent as Record<string, string[]>)[field] = [];
      (newContent as Record<string, string[]>)[field][arrayIndex] = value;
      setEditContent(newContent);
    };

    return (
      <div className="ai-expansion-section">
        <button
          type="button"
          className="expansion-header"
          onClick={(e) => toggleSection(sectionId, e)}
        >
          <h4>{t("aiExpansion.uniqueValueProposition")}</h4>
          <span className="expand-icon">
            {isExpanded ? "\u25BC" : "\u25B6"}
          </span>
        </button>

        {isExpanded && (
          <section
            className="expansion-content"
            aria-label={t("aiExpansion.ariaUvpContent")}
          >
            <div className="uvp-content">
              <div className="main-statement">
                <h5>{t("aiExpansion.mainUvpStatement")}</h5>
                {isEditing ? (
                  <textarea
                    value={content.mainStatement || ""}
                    onChange={(e) =>
                      updateUVPField("mainStatement", e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="edit-textarea uvp-statement-edit"
                    placeholder={t("aiExpansion.placeholders.mainStatement")}
                    rows={3}
                  />
                ) : (
                  <p className="uvp-statement">{content.mainStatement}</p>
                )}
              </div>

              <div className="key-benefits">
                <h5>{t("aiExpansion.keyBenefits")}</h5>
                {isEditing ? (
                  <div>
                    {content.keyBenefits?.map((benefit, index) => (
                      <input
                        key={`benefit-input-${index}-${benefit || ""}`}
                        type="text"
                        value={benefit || ""}
                        onChange={(e) =>
                          updateUVPArrayField(
                            "keyBenefits",
                            index,
                            e.target.value,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        placeholder={t("aiExpansion.indexedKeyBenefit", {
                          index: index + 1,
                        })}
                      />
                    ))}
                  </div>
                ) : (
                  <ul>
                    {content.keyBenefits?.map((benefit, index) => (
                      <li key={`benefit-li-${index}-${benefit || ""}`}>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="differentiation">
                <h5>{t("aiExpansion.differentiationFactors")}</h5>
                {isEditing ? (
                  <div>
                    {content.differentiationFactors?.map((factor, index) => (
                      <input
                        key={`factor-input-${index}-${factor || ""}`}
                        type="text"
                        value={factor || ""}
                        onChange={(e) =>
                          updateUVPArrayField(
                            "differentiationFactors",
                            index,
                            e.target.value,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        placeholder={t(
                          "aiExpansion.indexedDifferentiationFactor",
                          { index: index + 1 },
                        )}
                      />
                    ))}
                  </div>
                ) : (
                  <ul>
                    {content.differentiationFactors?.map((factor, index) => (
                      <li key={`factor-li-${index}-${factor || ""}`}>
                        {factor}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="target-audience">
                <h5>{t("aiExpansion.targetAudience")}</h5>
                {isEditing ? (
                  <textarea
                    value={content.targetAudience || ""}
                    onChange={(e) =>
                      updateUVPField("targetAudience", e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="edit-textarea"
                    placeholder={t(
                      "aiExpansion.placeholders.targetAudienceInput",
                    )}
                    rows={2}
                  />
                ) : (
                  <p>{content.targetAudience}</p>
                )}
              </div>

              <div className="value-metrics">
                <h5>{t("aiExpansion.valueMetrics")}</h5>
                {isEditing ? (
                  <div>
                    {content.valueMetrics?.map((metric, index) => (
                      <input
                        key={`metric-input-${index}-${metric || ""}`}
                        type="text"
                        value={metric || ""}
                        onChange={(e) =>
                          updateUVPArrayField(
                            "valueMetrics",
                            index,
                            e.target.value,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        placeholder={t("aiExpansion.indexedValueMetric", {
                          index: index + 1,
                        })}
                      />
                    ))}
                  </div>
                ) : (
                  <ul>
                    {content.valueMetrics?.map((metric, index) => (
                      <li key={`metric-li-${index}-${metric || ""}`}>
                        {metric}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    );
  };

  const renderAlternatives = (expansion: Expansion) => {
    const content =
      editingExpansion === expansion.id ? editContent : expansion.content;
    const sectionId = `alternatives-${expansion.id}`;
    const isExpanded = expandedSections.has(sectionId);
    const isEditing = editingExpansion === expansion.id;

    const updateAlternativeField = (
      altIndex: number,
      field: string,
      value: string,
    ) => {
      const newContent = { ...editContent };
      if (!newContent.alternatives) newContent.alternatives = [];
      if (!newContent.alternatives[altIndex])
        newContent.alternatives[altIndex] = {};
      (newContent.alternatives[altIndex] as Record<string, string>)[field] =
        value;
      setEditContent(newContent);
    };

    const updateAlternativeArrayField = (
      altIndex: number,
      field: string,
      arrayIndex: number,
      value: string,
    ) => {
      const newContent = { ...editContent };
      if (!newContent.alternatives) newContent.alternatives = [];
      if (!newContent.alternatives[altIndex])
        newContent.alternatives[altIndex] = {};
      const alt = newContent.alternatives[altIndex] as Record<string, string[]>;
      if (!alt[field]) alt[field] = [];
      alt[field][arrayIndex] = value;
      setEditContent(newContent);
    };

    return (
      <div className="ai-expansion-section">
        <button
          type="button"
          className="expansion-header"
          onClick={(e) => toggleSection(sectionId, e)}
        >
          <h4>{t("aiExpansion.alternativeApproaches")}</h4>
          <span className="expand-icon">
            {isExpanded ? "\u25BC" : "\u25B6"}
          </span>
        </button>

        {isExpanded && (
          <section
            className="expansion-content"
            aria-label={t("aiExpansion.ariaAlternativesContent")}
          >
            <div className="alternatives-grid">
              {content.alternatives?.map((alternative, index) => (
                <div
                  key={`alternative-${index}-${alternative.title || ""}`}
                  className="alternative-card"
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={alternative.title || ""}
                        onChange={(e) =>
                          updateAlternativeField(index, "title", e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="edit-input"
                        placeholder={t(
                          "aiExpansion.placeholders.alternativeTitle",
                        )}
                      />
                      <textarea
                        value={alternative.description || ""}
                        onChange={(e) =>
                          updateAlternativeField(
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="edit-textarea"
                        placeholder={t("aiExpansion.placeholders.description")}
                        rows={3}
                      />

                      <div className="alternative-section">
                        <strong>{t("aiExpansion.keyDifferences")}</strong>
                        {alternative.keyDifferences?.map((diff, i) => (
                          <input
                            key={`diff-input-${i}-${diff || ""}`}
                            type="text"
                            value={diff || ""}
                            onChange={(e) =>
                              updateAlternativeArrayField(
                                index,
                                "keyDifferences",
                                i,
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t("aiExpansion.indexedKeyDifference", {
                              index: i + 1,
                            })}
                          />
                        ))}
                      </div>

                      <div className="alternative-section">
                        <strong>{t("aiExpansion.advantages")}</strong>
                        {alternative.advantages?.map((adv, i) => (
                          <input
                            key={`adv-input-${i}-${adv || ""}`}
                            type="text"
                            value={adv || ""}
                            onChange={(e) =>
                              updateAlternativeArrayField(
                                index,
                                "advantages",
                                i,
                                e.target.value,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="edit-input"
                            placeholder={t("aiExpansion.indexedAdvantage", {
                              index: i + 1,
                            })}
                          />
                        ))}
                      </div>

                      <div className="alternative-section">
                        <strong>{t("aiExpansion.targetMarketChanges")}</strong>
                        <textarea
                          value={alternative.targetMarketChanges || ""}
                          onChange={(e) =>
                            updateAlternativeField(
                              index,
                              "targetMarketChanges",
                              e.target.value,
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="edit-textarea"
                          placeholder={t(
                            "aiExpansion.placeholders.targetMarketChanges",
                          )}
                          rows={2}
                        />
                      </div>

                      <div className="complexity-badge">
                        <select
                          value={
                            alternative.implementationComplexity || "medium"
                          }
                          onChange={(e) =>
                            updateAlternativeField(
                              index,
                              "implementationComplexity",
                              e.target.value,
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="edit-select"
                        >
                          <option value="low">
                            {t("aiExpansion.complexityLow")}
                          </option>
                          <option value="medium">
                            {t("aiExpansion.complexityMedium")}
                          </option>
                          <option value="high">
                            {t("aiExpansion.complexityHigh")}
                          </option>
                        </select>
                        <span> {t("aiExpansion.complexity")}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <h5>{alternative.title}</h5>
                      <p className="description">{alternative.description}</p>

                      <div className="alternative-section">
                        <strong>{t("aiExpansion.keyDifferences")}</strong>
                        <ul>
                          {alternative.keyDifferences?.map((diff, i) => (
                            <li key={`diff-li-${i}-${diff}`}>{diff}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="alternative-section">
                        <strong>{t("aiExpansion.advantages")}</strong>
                        <ul>
                          {alternative.advantages?.map((adv, i) => (
                            <li key={`adv-li-${i}-${adv}`}>{adv}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="alternative-section">
                        <strong>{t("aiExpansion.targetMarketChanges")}</strong>
                        <p>{alternative.targetMarketChanges}</p>
                      </div>

                      <div className="complexity-badge">
                        <span
                          className={`complexity ${alternative.implementationComplexity?.toLowerCase()}`}
                        >
                          {alternative.implementationComplexity}{" "}
                          {t("aiExpansion.complexity")}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  };

  const renderExpansion = (expansion: Expansion) => {
    switch (expansion.expansion_type) {
      case "swot":
        return renderSWOTAnalysis(expansion);
      case "personas":
        return renderPersonas(expansion);
      case "uvp":
        return renderUVP(expansion);
      case "alternatives":
        return renderAlternatives(expansion);
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "approved":
        return "#4caf50";
      case "rejected":
        return "#f44336";
      default:
        return "#ff9800";
    }
  };

  if (loading) {
    return (
      <div className="ai-expansion-loading">
        <div className="loading-spinner"></div>
        <h4>{t("aiExpansion.generatingExpansions")}</h4>
        <p>{t("aiExpansion.generatingDescription")}</p>
        <div className="generation-steps">
          <div className="step">{t("aiExpansion.swotAnalysis")}</div>
          <div className="step">{t("aiExpansion.userPersonas")}</div>
          <div className="step">{t("aiExpansion.uniqueValueProposition")}</div>
          <div className="step">{t("aiExpansion.alternativeApproaches")}</div>
        </div>
        <p style={{ marginTop: "16px", fontSize: "14px", color: "#888" }}>
          {t("aiExpansion.pleaseWait")}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-expansion-error">
        <p>
          {t("aiExpansion.error")} {error}
        </p>
        <button onClick={fetchExpansions}>{t("common:actions.retry")}</button>
      </div>
    );
  }

  if (expansions.length === 0) {
    return (
      <div className="ai-expansion-empty">
        <div className="empty-state-content">
          <div className="empty-icon">AI</div>
          <h4>{t("aiExpansion.noExpansionsYet")}</h4>
          <p>{t("aiExpansion.noExpansionsDescription")}</p>
          <button
            className="generate-expansions-btn"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                const response = await authFetch(
                  `/api/kanban/ideas/${ideaId}/expand`,
                  {
                    method: "POST",
                  },
                );

                const data = (await response.json()) as ApiResponse<unknown>;

                if (data.success) {
                  setLoading(true);
                  setError(null);

                  // Poll for completion
                  let attempts = 0;
                  const maxAttempts = 30;

                  const pollForCompletion = async () => {
                    try {
                      await fetchExpansions();

                      // Check if we have expansions now
                      const statusResponse = await authFetch(
                        `/api/kanban/ideas/${ideaId}/expansion-status`,
                      );
                      const statusData =
                        (await statusResponse.json()) as ApiResponse<StatusData>;

                      if (
                        statusData.success &&
                        statusData.data?.hasExpansions &&
                        statusData.data.expansionCount >= 4
                      ) {
                        setLoading(false);
                        return;
                      }

                      attempts++;
                      if (attempts < maxAttempts) {
                        setTimeout(pollForCompletion, 3000);
                      } else {
                        setLoading(false);
                        await fetchExpansions(); // Final fetch
                      }
                    } catch (pollError) {
                      console.error("Error polling for completion:", pollError);
                      setLoading(false);
                      await fetchExpansions();
                    }
                  };

                  setTimeout(pollForCompletion, 5000);
                } else {
                  alert(
                    t("aiExpansion.failedToGenerateWithError", {
                      error: data.error || data.message,
                    }),
                  );
                }
              } catch (genError) {
                console.error("Error generating AI expansions:", genError);
                alert(t("aiExpansion.failedToGenerate"));
              }
            }}
          >
            {t("aiExpansion.generateButton")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-expansion-display">
      <h3>{t("aiExpansion.title")}</h3>

      {expansions.map((expansion) => (
        <div key={expansion.id} className="expansion-item">
          <div className="expansion-status-bar">
            <span
              className="status-badge"
              style={{ backgroundColor: getStatusColor(expansion.status) }}
            >
              {expansion.status.toUpperCase()}
            </span>

            <div className="expansion-actions">
              {editingExpansion === expansion.id ? (
                <>
                  <button
                    className="save-btn"
                    onClick={(e) => handleSaveEdit(expansion.id, e)}
                  >
                    {t("common:actions.save")}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={(e) => handleCancelEdit(e)}
                  >
                    {t("common:actions.cancel")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="edit-btn"
                    onClick={(e) => handleEdit(expansion, e)}
                  >
                    {t("common:actions.edit")}
                  </button>
                  {expansion.status === "draft" && (
                    <>
                      <button
                        className="approve-btn"
                        onClick={(e) => handleApprove(expansion.id, e)}
                      >
                        {t("aiExpansion.approve")}
                      </button>
                      <button
                        className="reject-btn"
                        onClick={(e) => handleReject(expansion.id, e)}
                      >
                        {t("aiExpansion.reject")}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>

            {expansion.reviewed_by && (
              <span className="reviewer">
                {t("aiExpansion.reviewedBy")} {expansion.reviewer_name}
              </span>
            )}
          </div>

          {renderExpansion(expansion)}
        </div>
      ))}

      <style>{`
        .ai-expansion-display {
          padding: 15px;
          background: white;
          border-radius: 0 0 8px 8px;
          position: relative;
          z-index: 1;
        }

        .expansion-item {
          margin-bottom: 20px;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .expansion-status-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          background: #f5f5f5;
          border-bottom: 1px solid #e0e0e0;
        }

        .status-badge {
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }

        .expansion-actions {
          display: flex;
          gap: 10px;
        }

        .approve-btn, .reject-btn, .edit-btn, .save-btn, .cancel-btn {
          padding: 5px 10px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          margin-left: 5px;
          position: relative;
          z-index: 2;
        }

        .approve-btn {
          background: #4caf50;
          color: white;
        }

        .reject-btn, .cancel-btn {
          background: #f44336;
          color: white;
        }

        .edit-btn {
          background: #2196f3;
          color: white;
        }

        .save-btn {
          background: #4caf50;
          color: white;
        }

        .ai-expansion-section {
          border-bottom: 1px solid #e0e0e0;
        }

        .expansion-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          cursor: pointer;
          background: #fafafa;
          width: 100%;
          border: none;
        }

        .expansion-header:hover {
          background: #f0f0f0;
        }

        .expansion-content {
          padding: 15px;
        }

        .swot-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }

        .swot-quadrant {
          padding: 15px;
          border-radius: 8px;
          border-left: 4px solid;
        }

        .strengths { border-left-color: #4caf50; background: #f1f8e9; }
        .weaknesses { border-left-color: #ff9800; background: #fff3e0; }
        .opportunities { border-left-color: #2196f3; background: #e3f2fd; }
        .threats { border-left-color: #f44336; background: #ffebee; }

        .personas-grid, .alternatives-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 15px;
        }

        .persona-card, .alternative-card {
          padding: 15px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          background: white;
        }

        .persona-section, .alternative-section {
          margin: 10px 0;
        }

        .channel-tag {
          display: inline-block;
          background: #e3f2fd;
          color: #1976d2;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          margin: 2px;
        }

        .complexity {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }

        .complexity.low { background: #4caf50; color: white; }
        .complexity.medium { background: #ff9800; color: white; }
        .complexity.high { background: #f44336; color: white; }

        .uvp-statement {
          font-size: 18px;
          font-weight: bold;
          color: #1976d2;
          padding: 15px;
          background: #e3f2fd;
          border-radius: 8px;
          margin: 10px 0;
        }

        .ai-expansion-loading, .ai-expansion-error {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .ai-expansion-empty {
          text-align: center;
          padding: 40px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 2px dashed #e0e0e0;
        }

        .empty-state-content {
          max-width: 400px;
          margin: 0 auto;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state-content h4 {
          color: #333;
          margin: 0 0 12px 0;
          font-size: 18px;
        }

        .empty-state-content p {
          color: #666;
          margin: 0 0 24px 0;
          line-height: 1.5;
        }

        .generate-expansions-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .generate-expansions-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .generate-expansions-btn:active {
          transform: translateY(0);
        }

        .edit-input, .edit-textarea {
          width: 100%;
          padding: 8px 12px;
          border: 2px solid #2196f3;
          border-radius: 4px;
          font-size: 14px;
          margin-bottom: 8px;
          box-sizing: border-box;
        }

        .edit-input:focus, .edit-textarea:focus {
          outline: none;
          border-color: #1976d2;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }

        .edit-textarea {
          resize: vertical;
          font-family: inherit;
        }

        .uvp-statement-edit {
          font-size: 16px;
          font-weight: bold;
          color: #1976d2;
          background: #e3f2fd;
          border-radius: 8px;
          margin: 10px 0;
        }

        .edit-select {
          padding: 4px 8px;
          border: 2px solid #2196f3;
          border-radius: 4px;
          font-size: 12px;
          background: white;
          cursor: pointer;
        }

        .edit-select:focus {
          outline: none;
          border-color: #1976d2;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        .generation-steps {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin: 16px 0;
          max-width: 300px;
          margin-left: auto;
          margin-right: auto;
        }

        .step {
          background: #f0f0f0;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          color: #666;
        }

        .ai-expansion-loading h4 {
          color: #333;
          margin: 0 0 8px 0;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AIExpansionDisplay;
